import React from 'react';
import { Alert, Box, CircularProgress } from '@mui/material';
import { useLocation } from 'react-router-dom';
import LoginForm from '../components/Forms/LoginForm';
import RegisterForm from '../components/Forms/RegisterForm';
import ResetPasswordForm from '../components/Forms/ResetPasswordForm';
import VerifyAccount from '../components/Forms/VerifyAccount';
import { useOAuthReturn } from '../hooks/useOAuthReturn';
import { useTranslation } from 'react-i18next';
import CompleteGoogleProfileModal from '../components/Forms/CompleteGoogleProfileModal';
import CompleteMusicStylesModal from '../components/Forms/CompleteMusicStylesModal';

const AuthPage: React.FC = () => {
  const location = useLocation();

  // Keep compatibility with old language-prefixed paths
  const pathWithoutLang = location.pathname.replace(/^\/[a-z]{2}(\/|$)/, '/');

  const isLoginPage = pathWithoutLang === '/' || pathWithoutLang === '';
  const isRegisterPage = pathWithoutLang === '/register';
  const isResetPage = pathWithoutLang === '/reset';
  const isVerifyPage = pathWithoutLang === '/verify';
  const isUnknownAuthPath = !isLoginPage && !isRegisterPage && !isResetPage && !isVerifyPage;

  const shouldHandleOAuthReturn = isLoginPage || isUnknownAuthPath;
  const { t } = useTranslation();
  const {
    banner,
    continuing,
    profileCompletion,
    musicStyleUserId,
    completeProfile,
    completeMusicStyles,
    continueAfterAuthentication,
  } = useOAuthReturn(shouldHandleOAuthReturn);

  return (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="100vh">
      {banner ? (
        <Box sx={{ width: '100%', maxWidth: 420, px: 2, mb: 1 }}>
          <Alert severity={banner.severity}>{banner.message}</Alert>
        </Box>
      ) : null}
      {continuing ? (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CircularProgress size={28} />
          <Box component="span" sx={{ color: 'text.secondary' }}>
            {t('auth.googleSigningIn')}
          </Box>
        </Box>
      ) : null}
      {(isLoginPage || isUnknownAuthPath) && (
        <LoginForm onAuthenticated={continueAfterAuthentication} />
      )}
      {isRegisterPage && <RegisterForm />}
      {isResetPage && <ResetPasswordForm />}
      {isVerifyPage && <VerifyAccount onAuthenticated={continueAfterAuthentication} />}
      <CompleteGoogleProfileModal
        profile={profileCompletion}
        onComplete={completeProfile}
      />
      <CompleteMusicStylesModal
        userId={musicStyleUserId}
        onComplete={completeMusicStyles}
      />
    </Box>
  );
};

export default AuthPage;

